import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Package2, Truck, CheckCircle2, FileText } from "lucide-react";
import api from "../api";
import useNoIndex from "../hooks/useNoIndex";

const STATUS_LABELS = {
  paid: "Confirmed",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
};

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  useNoIndex();

  useEffect(() => {
    api
      .get(`/orders/${id}`)
      .then(({ data }) => setOrder(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load order"));
  }, [id]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="overline">UNAVAILABLE</div>
        <h1 className="font-heading text-3xl mt-3">{String(err)}</h1>
        <Link to="/account/orders" className="btn-ghost mt-8 inline-flex">
          <ArrowLeft size={14} /> Back to Orders
        </Link>
      </div>
    );
  }
  if (!order) return <div className="max-w-5xl mx-auto px-6 py-20 text-[#8A94A6]">Loading…</div>;

  const lines = order.pricing?.line_items || order.items || [];
  const status = (order.status || "paid").toLowerCase();
  const statusLabel = STATUS_LABELS[status] || order.status?.toUpperCase() || "PAID";

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 print:py-4">
      <div className="flex items-center justify-between mb-8 print:hidden">
        <Link to="/account/orders" className="overline hover:text-white">
          <ArrowLeft size={12} className="inline mr-1" /> ALL ORDERS
        </Link>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await api.get(`/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `ProSourceLabs-Invoice-${order.order_number || order.id.slice(0, 8)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                // eslint-disable-next-line no-alert
                alert("Invoice download failed: " + (e?.response?.data?.detail || e.message));
              }
            }}
            className="btn-ghost"
            data-testid="order-invoice-pdf-button"
          >
            <FileText size={14} /> Download Invoice
          </button>
          <button onClick={() => window.print()} className="btn-ghost" data-testid="order-print-button">
            <Printer size={14} /> Print Receipt
          </button>
        </div>
      </div>

      <div className="surface p-8 sm:p-12">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-white/10 pb-6">
          <div>
            <div className="overline">ORDER · {order.order_number || order.id.slice(0, 8).toUpperCase()}</div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-2 font-light">Receipt</h1>
            <div className="font-mono-tech text-xs text-[#5C657A] mt-2">
              {new Date(order.created_at).toLocaleString()}
            </div>
          </div>
          <div className="text-right">
            <div className="overline" style={{ fontSize: 10 }}>STATUS</div>
            <div className="font-mono-tech text-base text-[#00C3FF] mt-1 uppercase tracking-wider">
              {statusLabel}
            </div>
          </div>
        </div>

        {/* Tracking section */}
        {(order.tracking_number || order.carrier || status === "shipped" || status === "delivered") && (
          <div data-testid="order-tracking" className="mt-6 surface p-5 flex gap-4 items-start bg-[#14141C]">
            {status === "delivered" ? (
              <CheckCircle2 className="text-[#00C3FF] flex-shrink-0 mt-1" size={18} />
            ) : (
              <Truck className="text-[#00C3FF] flex-shrink-0 mt-1" size={18} />
            )}
            <div>
              <div className="overline">SHIPMENT</div>
              <div className="text-sm text-white mt-1">
                {order.carrier ? `${order.carrier} · ` : ""}
                {order.tracking_number ? (
                  <span className="font-mono-tech text-[#00C3FF]">{order.tracking_number}</span>
                ) : (
                  <span className="text-[#8A94A6]">Tracking number will appear when your package ships.</span>
                )}
              </div>
              {order.tracking_url && (
                <a
                  href={order.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="order-track-package-btn"
                  className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-[#00C3FF] text-[#05050A] font-mono-tech text-[10px] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
                >
                  Track Package →
                </a>
              )}
              {order.notes && (
                <div className="text-xs text-[#8A94A6] mt-2 italic">{order.notes}</div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8">
          <div className="overline mb-4">ITEMS · {lines.length}</div>
          <div className="space-y-3">
            {lines.map((li, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-3 py-3 border-b border-white/5">
                <div className="col-span-6">
                  <div className="font-heading text-base text-white">{li.name || li.product_id}</div>
                  <div className="font-mono-tech text-[10px] text-[#5C657A] uppercase tracking-wider mt-1">
                    {li.variant}{li.sku ? ` · SKU ${li.sku}` : ""}
                  </div>
                </div>
                <div className="col-span-2 font-mono-tech text-sm text-[#8A94A6] text-center">
                  ${Number(li.unit_price || 0).toFixed(2)}
                </div>
                <div className="col-span-2 font-mono-tech text-sm text-[#8A94A6] text-center">
                  × {li.quantity}
                </div>
                <div className="col-span-2 font-mono-tech text-sm text-white text-right">
                  ${Number(li.line_subtotal || li.unit_price * li.quantity || 0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 ml-auto max-w-sm space-y-1 text-sm">
          {order.pricing?.subtotal != null && (
            <div className="flex justify-between text-white/70">
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#5C657A]">Subtotal</span>
              <span className="font-mono-tech">${Number(order.pricing.subtotal).toFixed(2)}</span>
            </div>
          )}
          {order.pricing?.promo && (
            <div className="flex justify-between text-[#00C3FF]">
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em]">
                Promo · {order.pricing.promo.code}
              </span>
              <span className="font-mono-tech">-${Number(order.pricing.promo_discount).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline pt-3 border-t border-white/10 mt-3">
            <span className="overline">TOTAL</span>
            <span data-testid="order-total" className="font-mono-tech text-2xl">
              ${Number(order.amount).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-x-10 gap-y-3 text-[10px] font-mono-tech tracking-[0.2em] text-[#5C657A] uppercase">
          <span>For Research Use Only</span>
          <span>Not For Human Consumption</span>
          <span>Questions? support@prosourcelabs.org</span>
        </div>
      </div>
    </div>
  );
}
